import React from 'react';
import ErrorBoundary from '../ui/ErrorBoundary';
import LoadingState from '../ui/LoadingState';

const DashboardSection = ({
  children,
  title,
  subtitle,
  className = '',
  spacing = 'default',
  background = 'transparent',
  shadow = false,
  border = false,
  isLoading = false,
  error = null,
  onRetry,
  withErrorBoundary = false,
  headerActions,
  collapsible = false,
  initialCollapsed = false,
  id
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(initialCollapsed);

  const getSpacingClasses = (spacing) => {
    switch (spacing) {
      case 'none':
        return '';
      case 'tight':
        return 'mb-6';
      case 'loose':
        return 'mb-12';
      case 'extra-loose':
        return 'mb-16';
      case 'default':
      default:
        return 'mb-8';
    }
  };

  const getBackgroundClasses = (background) => {
    const backgroundMap = {
      'transparent': '',
      'white': 'bg-crystal-white',
      'gray': 'bg-silver-mist',
      'gray-light': 'bg-crystal-white/50',
      'blue': 'bg-info/5',
      'green': 'bg-fresh-leaf/5',
      'yellow': 'bg-warning/5',
      'red': 'bg-error/5'
    };
    return backgroundMap[background] || '';
  };

  const sectionClasses = `
    ${getSpacingClasses(spacing)}
    ${getBackgroundClasses(background)}
    ${shadow ? 'shadow-soft' : ''}
    ${border ? 'border border-woodland-moss/20' : ''}
    transition-all duration-300 ease-in-out
    ${className}
  `.trim();

  const sectionStyle = background !== 'transparent' ? { borderRadius: '24px' } : {};

  const contentClasses = `
    ${background !== 'transparent' ? 'p-6 lg:p-8' : ''}
  `.trim();

  const toggleCollapse = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const SectionHeader = () => {
    if (!title && !headerActions) return null;

    return (
      <div className={`flex items-start justify-between gap-4 ${background !== 'transparent' ? 'pb-6 border-b border-woodland-moss/15 mb-6' : 'mb-6'}`}>
        <div className="flex-1 min-w-0">
          {title && (
            <div className="flex items-center gap-3">
              <h2 className="text-xl lg:text-2xl font-display font-semibold text-deep-jungle tracking-tight">
                {title}
              </h2>
              {collapsible && (
                <button
                  onClick={toggleCollapse}
                  className="flex-shrink-0 p-2 text-steel hover:text-deep-jungle focus:outline-none focus:ring-2 focus:ring-fresh-leaf/30 rounded-xl transition-colors duration-200"
                  aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
                >
                  <svg 
                    className={`h-5 w-5 transform transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>
          )}
          {subtitle && (
            <p className="mt-2 text-sm lg:text-base text-steel leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
        {headerActions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {headerActions}
          </div>
        )}
      </div>
    );
  };

  const SectionContent = () => {
    if (isLoading) {
      return (
        <div className="py-12">
          <LoadingState 
            text="Loading..." 
            variant="spinner"
            className="text-steel"
          />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12 px-4">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-error/10 rounded-2xl mb-4">
            <svg className="w-6 h-6 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-steel mb-4 font-medium">
            {typeof error === 'string' ? error : 'Unable to load content'}
          </p>
          {onRetry && (
            <button 
              onClick={onRetry}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-fresh-leaf hover:text-forest-fern focus:outline-none focus:ring-2 focus:ring-fresh-leaf/30 rounded-xl transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try again
            </button>
          )}
        </div>
      );
    }

    if (collapsible && isCollapsed) {
      return null;
    }

    return (
      <div className={`transition-all duration-300 ${collapsible && !isCollapsed ? 'animate-in fade-in slide-in-from-top-2' : ''}`}>
        {children}
      </div>
    );
  };

  const SectionWrapper = () => (
    <section 
      className={sectionClasses}
      style={sectionStyle}
      id={id}
      aria-labelledby={title ? `${id}-title` : undefined}
    >
      <div className={contentClasses}>
        <SectionHeader />
        <SectionContent />
      </div>
    </section>
  );

  if (withErrorBoundary) {
    return (
      <ErrorBoundary
        variant="card"
        title="Unable to load section"
        message="Something went wrong while loading this content."
        onRetry={onRetry}
      >
        <SectionWrapper />
      </ErrorBoundary>
    );
  }

  return <SectionWrapper />;
};

// Preset section variants for common dashboard sections
export const StatisticsSection = ({ children, ...props }) => (
  <DashboardSection
    background="white"
    shadow={true}
    spacing="default"
    {...props}
  >
    {children}
  </DashboardSection>
);

export const QuickActionsSection = ({ children, ...props }) => (
  <DashboardSection
    background="transparent"
    spacing="default"
    {...props}
  >
    {children}
  </DashboardSection>
);

export const RecentActivitySection = ({ children, ...props }) => (
  <DashboardSection
    background="white"
    shadow={true}
    spacing="tight"
    withErrorBoundary={true}
    {...props}
  >
    {children}
  </DashboardSection>
);

export const CollapsibleSection = ({ children, ...props }) => (
  <DashboardSection
    background="white"
    border={true}
    collapsible={true}
    withErrorBoundary={true}
    {...props}
  >
    {children}
  </DashboardSection>
);

export const CardSection = ({ children, ...props }) => (
  <DashboardSection
    background="white"
    shadow={true}
    border={false}
    {...props}
  >
    {children}
  </DashboardSection>
);

export default DashboardSection;
